import EventEmitter from 'events';
import Device from './Device';

import LoggerWrapper from '../../modules/logger-wrapper';

const logger = new LoggerWrapper('MEDIA_CORE', 'Devices', LoggerWrapper.DEBUG);
logger.setRemoteVerbosity(LoggerWrapper.DEBUG);
class Devices extends EventEmitter {
	constructor() {
		super(arguments);
		this.devices = [];
		this.promise = null;
		this.initialize();
		this._addListeners();
	}

	_addListeners() {
		logger.debug('Initializing device listeners...');
		navigator.mediaDevices?.addEventListener('devicechange', () => this.updateDevices(false));
	}

	getDevices(kind = null) {
		return kind ? this.devices.filter((d) => d.kind === kind) : this.devices;
	}

	getDevice(deviceId, kind = null) {
		const device = this.devices.find((d) =>
			kind ? d.deviceId === deviceId && d.kind === kind : d.deviceId === deviceId
		);
		// we want to return non default device for audio input
		// for all other cases return device
		if (!device || kind !== 'audioinput') {
			return device;
		}
		// audioinput case - return non default device
		const deviceDetails = { groupId: device._device.groupId, kind };

		// add label only if the default is the one selected.
		if (device.deviceId !== 'default') {
			deviceDetails.label = device.label;
		}
		return this.getDeviceByDetails(deviceDetails);
	}

	/**
	 * Finds and returns the non default listing of a device given group id, kind and optionally label
	 */
	getDeviceByDetails({ groupId, kind, label }) {
		return this.devices.find((d) =>
			!!label
				? d._device.groupId === groupId && d.kind === kind && d.label === label && d.deviceId !== 'default'
				: d._device.groupId === groupId && d.kind === kind && d.deviceId !== 'default'
		);
	}

	getDeviceIdByDetails({ groupId, kind, label }) {
		return this.getDeviceByDetails({ groupId, kind, label })?.deviceId;
	}

	getDeviceMetadata(deviceId, kind = null) {
		const device = this.getDevice(deviceId, kind);
		let result = null;
		if (device) {
			result = {
				label: device.label,
				deviceId: device.deviceId,
				capabilities: device.capabilities,
			};
		}
		return result;
	}

	_findConnectedDevices(devices, previousDevices) {
		const difference = _.differenceBy(previousDevices, devices, 'deviceId');
		if (difference.length > 0) {
			logger.debug('Devices connected...', difference);
			this.emit('devices:connected', difference);
		}
	}

	_findDisconnectedDevices(devices, previousDevices) {
		const difference = _.differenceBy(devices, previousDevices, 'deviceId');
		if (difference.length > 0) {
			logger.debug('Devices disconnected...', difference);
			this.emit('devices:discconected', difference);
		}
	}

	async initialize() {
		const initializing = {};
		this.promise = new Promise((resolve, reject) => {
			initializing.resolve = resolve;
			initializing.reject = reject;
		});
		await this.updateDevices(true);
		initializing.resolve();
	}

	async updateDevices(firstTime = false) {
		logger.debug('Updating connected devices...');
		const devices = await Devices.requestDeviceList();
		logger.debug(`${devices.length} devices available detected.`, { devices });

		const [...previousDevices] = firstTime ? devices : this.devices;
		this.devices = devices.map((d) => new Device(d));

		if (!firstTime) {
			this._findConnectedDevices(previousDevices, devices);
			this._findDisconnectedDevices(previousDevices, devices);
		}

		this.emit('devices:changed', this.devices);
	}

	static async requestDeviceList() {
		return await navigator.mediaDevices.enumerateDevices();
	}
}

export default Devices;
